import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  AspectRatio,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  Image,
  Stack,
  TabList,
  TabPanels,
  Tabs,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useRef } from "react";
import { Helmet } from "react-helmet";
import { IoLocationSharp } from "react-icons/io5";
import { TiMinus, TiPlus } from "react-icons/ti";
import { useParams } from "react-router-dom";
import { ImageCarousel } from "../components/ImageCarousel";
import { DesktopTabPanel, StyledTab } from "../components/ParkPageComponents";
import { BLUE_COLOR, MAX_CONTENT_WIDTH, PARAGRAPH_SPACING, SITE_MIN_MARGIN } from "../const";
import { useGetParkDetails } from "../hooks/useGetParkDetails";
import { useParkPageTabData } from "../hooks/useParkPageTabData";
import "../Slider.css";
import { decodeHtmlEntities } from "../utils/decodeHtmlEntities";
import { getParkPageTitle } from "../utils/getParkPageTitle";

export const ParkPage: React.FC = () => {
  const { park } = useParams();
  const { data: parkDetails } = useGetParkDetails();
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false });

  const videoRef = useRef(null);

  const stopVideo = (element: any) => {
    element?.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
  };

  const slidesToShow = useBreakpointValue({ base: 1, md: 2, lg: 3 }, { ssr: false }) || 1;

  const { tabData } = useParkPageTabData(videoRef);
  const availableTabs = tabData.filter(({ condition }) => condition);

  if (!parkDetails) {
    return null;
  }

  const { imgs } = parkDetails;

  const seasonText = parkDetails?.season === "All Year" ? "Open All Year" : `Opening dates: ${parkDetails?.season}`;

  const decodedStr = parkDetails?.overview?.replace(/&lt;/g, "<").replace(/&gt;/g, ">");
  const firstParagraph = decodedStr.match(/<p>(.*?)<\/p>/)?.[1] || "";

  return (
    <>
      <Helmet>
        <title>
          {getParkPageTitle(park!)}, {parkDetails.sAdd} - Holiday Homes for Sale
        </title>
        <meta name="description" content={firstParagraph} />
      </Helmet>
      <Box bg="gray.50" borderBottomWidth={1} px={SITE_MIN_MARGIN} py={4}>
        <Box maxW={MAX_CONTENT_WIDTH} mx="auto">
          <Heading as="h1">{parkDetails.name}</Heading>
          {parkDetails?.sAdd && (
            <Flex alignItems="center">
              <Icon as={IoLocationSharp} color={BLUE_COLOR} boxSize="20px" />
              <Text>{parkDetails?.sAdd}</Text>
            </Flex>
          )}
          {parkDetails?.season && <Text>{seasonText}</Text>}
        </Box>
      </Box>

      <Box mx="auto" mt={6} mb={2} w="100%" p={4}>
        <ImageCarousel extraSettings={{ slidesToShow }} className={slidesToShow > 1 ? "full-width-carousel" : ""}>
          {imgs.map((url, i) => (
            <AspectRatio key={i} ratio={4 / 3} w="100%">
              <Image src={url} boxSize="100%" objectFit="cover" />
            </AspectRatio>
          ))}
        </ImageCarousel>
      </Box>

      {!isMobile && (
        <Grid
          templateColumns={{ base: "1fr", lg: "repeat(4, 1fr)" }}
          gap={6}
          py={4}
          px={SITE_MIN_MARGIN}
          maxW={MAX_CONTENT_WIDTH}
          mx="auto"
          w="100%"
        >
          <GridItem colSpan={{ base: 1, lg: 3 }} order={{ base: 2, lg: 1 }}>
            <Tabs isFitted variant="unstyled" onChange={() => stopVideo(videoRef?.current)}>
              <Stack gap={4}>
                <TabList borderWidth={1}>
                  {availableTabs.map(({ tabTitle }) => (
                    <StyledTab key={tabTitle}>{tabTitle}</StyledTab>
                  ))}
                </TabList>

                <TabPanels>
                  {availableTabs.map(({ tabTitle, showInCard, content }) => (
                    <DesktopTabPanel key={tabTitle} showInCard={showInCard} heading={tabTitle}>
                      {content}
                    </DesktopTabPanel>
                  ))}
                </TabPanels>
              </Stack>
            </Tabs>
          </GridItem>

          {parkDetails.overview?.length > 0 && (
            <GridItem colSpan={1} order={{ base: 1, lg: 2 }}>
              <Box mb={4}>
                <Text
                  sx={PARAGRAPH_SPACING}
                  dangerouslySetInnerHTML={{
                    __html: decodeHtmlEntities(parkDetails.overview),
                  }}
                />
              </Box>
            </GridItem>
          )}
        </Grid>
      )}

      {isMobile && (
        <Accordion defaultIndex={[1]} allowMultiple px={4}>
          {availableTabs.map(({ tabTitle, content }, i) => (
            <AccordionItem key={i} borderTop={0}>
              {({ isExpanded }) => (
                <>
                  <h2>
                    <AccordionButton
                      h="60px"
                      p={4}
                      borderWidth={1}
                      borderColor="rgb(237, 237, 237)"
                      mb="-1px"
                      _hover={{ bg: BLUE_COLOR, color: "white" }}
                      _expanded={{ bg: "#5B5B5A", color: "white" }}
                    >
                      <Box as="span" flex="1" textAlign="left" fontSize="18px">
                        {tabTitle}
                      </Box>
                      {isExpanded ? <TiMinus /> : <TiPlus />}
                    </AccordionButton>
                  </h2>
                  <AccordionPanel p={4} borderLeftWidth={1} borderRightWidth={1}>
                    {content}
                  </AccordionPanel>
                </>
              )}
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </>
  );
};
